import Data from 'app-core/Data';
import Promise from 'bluebird';
import _ from 'lodash';
import config from 'app-core/config';
import { VOICE_TYPES } from 'app-core/constants';

let loadSpeechVoicesPromise = null;
let loadSpeechVoicesFinalPromise = null;
let loadedLanguages = [];
let loadedVoices = [];
let voiceTypeSettings = [];
let voiceTypeModels = [];
let systemLanguages = [];
let ttsProviders = [];
let voicesLoadingState = 0; //2 is loaded all
let preventVoicesAutoLoad = false;
let onLoad = [];

const __TEST_JENNY_AS_CUSTOM_VOICE__ = __DEBUG__ && false;
const __TEST_VOICE__ = 'en-US-JennyNeural';
const STATE_ACTIVE = 0;
const STATE_ENABLING = 1;
const STATE_SUSPENDING = 2;
const STATE_SUSPENDED = 3;

let activatePromises = {};
function activateVoice(voice, preventResume, voiceStyle) {
	if (!voice) return Promise.resolve(voice);
	if (!voice.isCustom) {
		return Promise.resolve(true);
	} else {
		let activateId = `${voice.uniqueId}_${voiceStyle || 'default'}`;

		if (activatePromises[activateId]) return activatePromises[activateId];

		let res = null;
		activatePromises[activateId] = (new Promise(r => res = r)).finally(() => {
			delete activatePromises[activateId];
			delete voice.activateResolve;
		});

		voice.activateResolve = res;

		getCustomVoiceStatus(voice.voice?.Oid, voiceStyle)
			.then(state => {
				if (__DEBUG__)
					console.debug('getCustomVoiceStatus', state);

				if (state !== STATE_ACTIVE) {
					voice.isActive = false;

					if (__DEBUG__)
						console.debug('Resume', state);

					if (!preventResume) {
						if (!voice.isResuming) {
							if (__DEBUG__)
								window.activatingVoice = voice;

							if (state !== STATE_SUSPENDING) {
								voice.isResuming = true;
								if (voice.activateTimer)
									clearInterval(voice.activateTimer);

								voice.activateTimer = setInterval(async () => {
									if (await getIsCustomVoiceActive(voice.voice?.Oid, voiceStyle) || voice.isActive) {
										clearInterval(voice.activateTimer);
										voice.isActive = true;
										res(true);
									}
								}, 20000);

								//Resume only if state suspended
								if (state === STATE_SUSPENDED) {
									resumeCustomVoiceActive(voice.voice?.Oid, voiceStyle).catch(() => voice.isResuming = false);
								}

								let msg = config.getVueApp().$tCaption('The voice you have selected (VOICE_NAME) is in sleep mode. We are preparing your premium voice.', 'AudioTracker').replace('VOICE_NAME', voice.displayName);
								config.getVueApp().$notifyWarning(config.getVueApp().$tCaption('The voice you have selected (VOICE_NAME) is in sleep mode. We are preparing your premium voice.', 'AudioTracker').replace('VOICE_NAME', voice.displayName), 10000);
								config.getVueApp().$eventsBus.$emit('notification-busy', true);
								config.getVueApp().$eventsBus.$emit('notification-message', { message: msg });

								return null;
							}
						}
					}
				} else {
					voice.isResuming = false;
					voice.isActive = true;
					res(true);
				}

				res(false);
				return null;
			})
			.catch(() => res(false));
		return activatePromises[activateId];
	}
}

function getIsCustomVoiceActive(voiceId, style) {
	if (__TEST_JENNY_AS_CUSTOM_VOICE__) return Promise.resolve(window.testVoice?.isActive);

	let styleParam = style ? `&style=${style}` : '';
	return Data.getExternalApi('Voice/IsCustomVoiceActive', `voiceId=${voiceId}${styleParam}`);
}

function getCustomVoiceStatus(voiceId, style) {
	if (__TEST_JENNY_AS_CUSTOM_VOICE__) return Promise.resolve(window.testVoice?.isActive ? STATE_ACTIVE : STATE_SUSPENDED);

	let styleParam = style ? `&style=${style}` : '';
	return Data.getExternalApi('Voice/GetCustomVoiceStatus', `voiceId=${voiceId}${styleParam}`);
}

function resumeCustomVoiceActive(voiceId, style) {
	if (__TEST_JENNY_AS_CUSTOM_VOICE__) return Promise.resolve(null);

	let styleParam = style ? `&style=${style}` : '';
	return Data.getExternalApi('Voice/ResumeCustomVoice', `voiceId=${voiceId}${styleParam}`).then(r => {
		if (__DEBUG__)
			console.debug('Voice resume finished', voiceId);
	});
}

function suspendCustomVoiceActive(voiceId, style) {
	let styleParam = style ? `&style=${style}` : '';
	return Data.getExternalApi('Voice/SuspendCustomVoice', `voiceId=${voiceId}${styleParam}`).then(r => {
		if (__DEBUG__)
			console.debug('Voice suspend finished', voiceId);
	});
}

function findVoice(id, language, gender, isCustom, prefilteredVoices) {
	let res = (prefilteredVoices || loadedVoices)?.filter(f =>
		(id == void 0 || f.id === id || f.uniqueId === id) &&
		(language == void 0 || f.language?.locale === getLanguage(language)) &&
		(gender == void 0 || f.voice?.Gender === gender) &&
		(isCustom == void 0 || f.isCustom === isCustom)
	)[0];

	if (__DEBUG__)
		console.debug('findVoice', { id, language, gender, isCustom, prefilteredVoices, res });

	return res;
}

function findDefaultVoice(language, gender, ssml) {
	if (ssml && ssml.indexOf('<mstts:voiceconversion') > -1) {
		return findVoice('zh-CN-XiaoxiaoNeural');
	}

	if (__TEST_JENNY_AS_CUSTOM_VOICE__) return findVoice(void 0, 'en-US', 'Male', false);

	if (!language)
		language = 'en-US';

	if (!gender)
		gender = 'Female';

	let voice = null;

	let getVoice = (voices) => {
		if (__DEBUG__)
			console.debug('getVoice1', { voices });
		if (!voice)
			voice = findVoice(null, language, gender, false, voices);
		if (!voice)
			voice = findVoice(null, language, null, false, voices);
		if (!voice)
			voice = findVoice(null, null, gender, false, voices);
		if (!voice)
			voice = findVoice(null, null, null, false, voices);	
	}

	//Search first for MsTTS and sort by rating desc
	getVoice(_.orderBy(loadedVoices.filter(f => f.provider?.ProviderType === 0), [f => f.voice?.Rating || 0], ['desc']));

	if (!voice) 
		getVoice(loadedVoices);

	if (!voice && language === 'en-US') {
		if (gender === 'Female')
			voice = loadedVoices?.filter(f => f.id === 'en-US-JennyNeural' || f.voice?.ShortName?.indexOf('en-US, JennyNeural') > -1)[0];
		else if (gender === 'Male')
			voice = loadedVoices?.filter(f => f.id === 'en-US-GuyNeural' || f.voice?.ShortName?.indexOf('en-US, GuyNeural') > -1)[0];
	}

	return voice;
}

function calculateVoiceReadingRate(voice) {
	let langWPM = voice?.language?.avgWordsPerMin || 0;
	let langSpeedAdjustFactor = voice?.language?.speedAdjustFactor || 1;
	let langMaxSpeedFactor = voice?.language?.maxSpeedFactor;
	let voiceWPM = voice?.wordCount || 0;

	let voiceReadingRateCalculated = Math.round((langWPM * langSpeedAdjustFactor / voiceWPM) * 100) / 100;
	let voiceReadingRate = 1;

	if (langWPM && langSpeedAdjustFactor && voiceWPM) {
		voiceReadingRate = Math.min(voiceReadingRateCalculated, langMaxSpeedFactor || 2);
	}

	return {
		langWPM,
		langSpeedAdjustFactor,
		langMaxSpeedFactor,
		langTotal: langWPM * langSpeedAdjustFactor,
		voiceWPM,
		voiceReadingRate,
		voiceReadingRateCalculated
	}
}

function getVoiceLoadingPormises() {
	return {
		loadSpeechVoicesPromise,
		loadSpeechVoicesFinalPromise
	};
}

let dictionariesPromise = null;
function loadSpeechVoices(force, locale) {
	if (__DEBUG__)
		console.debug('loadSpeechVoices', { locale });

	let releaseOnLoad = () => {
		onLoad.map(f => {
			f.setInfo(voicesLoadingState);
			f.resolve(loadedVoices);
		});

		if (voicesLoadingState === 2) {
			onLoad = [];
		}
	}

	if (loadSpeechVoicesPromise && !force) {
		return loadSpeechVoicesPromise.then(() => loadedVoices).finally(() => {
			releaseOnLoad();
		});
	} else {
		if (__DEBUG__)
			console.debug('---------------- LOAD SPEECH VOICES');

		let processLoadedVoices = async(r, filter) => {
			if (__DEBUG__)
				console.debug('---------------- Voices loaded', { r });

			const languageNames = new Intl.DisplayNames(['en'], { type: 'language', languageDisplay: 'standard' });
			const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

			let processLang = (item) => {
				let sysLang = systemLanguages.find(f => f.Code === item.locale);
				if (sysLang) {
					item.voiceTargetRating = sysLang.VoiceTargetRating;
					item.minCountVoices = sysLang.MinCountVoices;
					item.speedAdjustFactor = sysLang.SpeedAdjustFactor || 1;
					item.avgWordsPerMin = sysLang.AvgWordsPerMin || 0;
					item.maxSpeedFactor = sysLang.MaxSpeedFactor;
				}
				return item;
			};

			const processInChunks = async (data, chunkSize, callback) => {
				for (let i = 0; i < data.length; i += chunkSize) {
					const chunk = data.slice(i, i + chunkSize);
					await new Promise(resolve => setTimeout(resolve, 0));
					chunk.forEach(callback);
				}
			};
		
			let languages = [];
			await processInChunks(_.uniqBy(r, 'Locale'), 10, (it) => {
				let language = null;
				let region = null;
				let text = null;
				let voiceTargetRating = 0;
				let minCountVoices = 0;

				try {
					let lanReg = `${it.Locale}`.split('-');
					language = languageNames.of(lanReg[0] || 'en');
					region = regionNames.of(lanReg[1] || 'US');
					text = languageNames.of(it.Locale);

					let sysLang = systemLanguages.find(f => f.Code === lanReg);
					if (sysLang) {
						voiceTargetRating = sysLang.VoiceTargetRating;
						minCountVoices = sysLang.MinCountVoices;
					}
				} catch (ex) {
					if (__DEBUG__) console.error('Voices error', { ex, it, locale: it.Locale });
				}

				languages.push(processLang({
					id: it.Locale?.replace('-', ''),
					locale: it.Locale,
					text: text || it.Locale,
					name: language,
					region: region,
					countryCode: it.Locale?.split('-')[1],
					voiceTargetRating,
					minCountVoices
				}));
			});

			let voices = [];
			await processInChunks(r, 10, (it) => {
				let language = languages.filter(f => f.locale === it.Locale)[0] || { id: it.Locale, locale: it.Locale, text: it.Locale, name: it.Locale, countryCode: it.Locale };

				if (__TEST_JENNY_AS_CUSTOM_VOICE__ && it.ShortName === __TEST_VOICE__) {
					it.IsCustom = true;
					it.HasCustomVoiceAccess = true;
					it.WordCount = 100;
					language.avgWordsPerMin = 180;
					language.maxSpeedFactor = 1.1;
				}

				let ssmlStyles = (it.VoiceStyles || []).filter(f => !f.HasBackendVoice);
				(it.VoiceStyles || []).map(f => {
					f.id = f.Name;
					f.label = Data.capitalize(f.Name);
				});

				let voiceType = VOICE_TYPES.all.find(f => f.code === it.VoiceType || f.id === it.VoiceType);
				let voice = {
					uniqueId: `${it.ShortName}_${it.Locale}`,
					id: it.ShortName,
					isNeural: voiceType && voiceType.code.indexOf('Neural') > -1,
					isCustom: it.IsCustom,
					isCustomAvailable: it.HasCustomVoiceAccess,
					baseRate: it.BaseRate,
					displayName: it.DisplayName,
					locale: it.Locale,
					styleList: ssmlStyles || [],
					mainLanguage: language,
					language,
					voice: it,
					text: `${it.DisplayName}, ${language?.name} (${language?.region})`,
					isHuman: voiceType?.code?.indexOf('Human') > -1,
					isActive: !it.IsCustom,
					isResuming: false,
					settings: voiceTypeSettings.find(f => f.VoiceType === voiceType?.id),
					allSettings: voiceTypeSettings.filter(f => f.VoiceType === voiceType?.id),
					voiceType,
					wordCount: it.WordCount || 120,
					provider: ttsProviders.find(f => f.ProviderType === it.TtsProviderType)
				};

				let isResuming = false;
				Object.defineProperty(voice, 'isResuming', {
					get: () => isResuming,
					set: (v) => {
						isResuming = v;
						config.getVueApp().$eventsBus?.$emit('custom-voice-resume', voice, v);
					}
				});

				Object.defineProperty(voice, 'readingRateInfo', {
					get: () => calculateVoiceReadingRate(voice)
				});

				let isActive = it.IsCustom ? null : true;
				Object.defineProperty(voice, 'isActive', {
					get: () => isActive,
					set: (v) => {
						isActive = v;
						if (voice?.isCustom) {
							if (v) {
								voice.isResuming = false;
								config.getVueApp().$eventsBus?.$emit('notification-busy', false);
							}
							config.getVueApp().$eventsBus?.$emit('custom-voice-active', voice, v);
						}

						if (voice.activateResolve && v) {
							voice.activateResolve(true);
						}
					}
				});

				const activate = (preventResume) => activateVoice(voice, preventResume);
				Object.defineProperty(voice, '__activate', {
					get: () => activate
				});

				const supportsTag = (tag, attribute, model) => tagIsSupported(voice, tag, attribute, model);
				Object.defineProperty(voice, 'supportsTag', {
					get: () => supportsTag
				});

				voices.push(voice);

				if (__TEST_JENNY_AS_CUSTOM_VOICE__ && it.ShortName === __TEST_VOICE__) {
					window.testVoice = voice;
				}
			});

			loadedVoices.push(...(filter ? voices.filter(filter) : voices));
			loadedLanguages.push(...(filter ? languages.filter(filter) : languages));

			if (__DEBUG__) console.debug(`processLoadedVoices ${voices?.length}`, { voices, languages });
		}

		voicesLoadingState = 0;

		if (!dictionariesPromise) {
			dictionariesPromise = Promise.all([
				(async () => voiceTypeSettings = await Data.getExternalApi('VoiceTypeSettings/List', 9999))(),
				(async () => voiceTypeModels = (await Data.getExternalApi('TextToSpeechProviderModel/List', 9999))?.Results || [])(),
				(async () => systemLanguages = (await Data.getExternalApi('Language/List', 9999))?.Results || [])(),
				(async () => ttsProviders = (await Data.getExternalApi('TextToSpeechProvider/list', 9999))?.Results || [])()
			])
		}
		
		//Primary load locale voice if specified
		loadSpeechVoicesPromise = Data.getSpeechVoices(locale)
			.then(async r => {
				await dictionariesPromise;

				if (!r || !Array.isArray(r)) {
					loadSpeechVoicesPromise = null;
					return loadedVoices;
				}
				
				if (!r.find(f => f.ShortName === 'en-US-ZeroshotOverDubNeural')) {
					r.push({
						ShortName: 'en-US-ZeroshotOverDubNeural',
						Locale: 'en-US',
						DisplayName: 'Zeroshot Overdub'
					});
				}
				
				await processLoadedVoices(r);
				voicesLoadingState++;

				releaseOnLoad();
			});

		loadSpeechVoicesFinalPromise = loadSpeechVoicesPromise;

		//Load other voices if specified in parallel
		if (locale) {
			loadSpeechVoicesFinalPromise = Data.getSpeechVoices()
				.then(async r => {
					await dictionariesPromise;

					await processLoadedVoices(r, (f) => f.locale !== locale);
					voicesLoadingState++;

					releaseOnLoad();
				});
		} else {
			voicesLoadingState++;
			releaseOnLoad();
		}

		return loadSpeechVoicesPromise.then(() => loadedVoices);
	}
}

function getVoices(lang) {
	return _.orderBy(
		loadedVoices
			.filter(f => f.locale.replace('-', '').startsWith(lang))
			.map(f => {
				return {
					...f,
					text: `${f.displayName}`,
					groupName: f.isCustom ?  config.getVueApp().$tCaption('Premium') :  config.getVueApp().$tCaption('Standard'),
				}
			}),
		v => v.text
	);
}

function getLanguage(val) {
	let language = val;
	if (language?.length === 4 && language.indexOf('-') === -1) {
		language = `${language.slice(0,2)}-${language.slice(2,4)}`;
	}
	if (language?.length === 5 && language.indexOf('-') === -1) {
		language = `${language.slice(0,3)}-${language.slice(3,5)}`;
	}
	return language;
}

async function getVoicesAdjectives(opts) {
	const adjectives = [];
	(await loadSpeechVoices()).filter(f => f.voice?.Adjectives?.length).map(f => f.voice.Adjectives).map(voiceAdjectives => {
		voiceAdjectives.map(a => {
			if (!adjectives.includes(a?.Name || a?.Key)) {
				adjectives.push(opts?.full ? a : (a?.Name || a?.Key));
			}
		})
	});

	return _.orderBy(adjectives, f => f);
}

function findVoicesByAdjectives(adjectives, voices) {
	return (voices || loadedVoices).filter(f => f.voice?.Adjectives && adjectives.some(a => f.voice?.Adjectives.some(x => `${x.Name}`.toLowerCase() === `${a}`.toLowerCase() || `${x.Key}`.toLowerCase() === `${a}`.toLowerCase())));
}

async function getTtsProviders() {
	await this.loadSpeechVoices();
	return ttsProviders;
}

function tagIsSupported(voice, tag, attribute, ttsModelCode) {
	let settings = (voice?.allSettings || []).find(f => f.TtsModelCode === (ttsModelCode || null)) || voice?.settings;
	if (!settings) return true;

	if (['adthos:wbs', 'adthos:variativetext'].includes(tag)) return true;
	
	let res = settings.SupportSsml;

	if (!res) {
		let supportedOptions = (settings?.SupportedOptions || '').split(',').map(f => f?.toLowerCase()?.trim()).filter(f => f);
		res = supportedOptions.includes(tag?.toLowerCase());
	} else {
		if (settings?.SupportedTags?.length) {
			if (!attribute) {
				res = settings.SupportedTags.some(f => f.Name?.toLowerCase() === tag?.toLowerCase());
			} else {
				res = settings.SupportedTags.some(f => f.Name?.toLowerCase() === tag?.toLowerCase() && (!f.Attributes?.length || f.Attributes.map(f => f.toLowerCase()).includes(attribute)));
			}
		}
	}

	if (__DEBUG__)
		console.debug(`Supported tag ${tag}: ${res}`, { r: settings?.SupportedTags?.some(f => f.Name?.toLowerCase() === tag?.toLowerCase()) });

	return res;
}

function setVoicesPreventAutoLoad(v) {
	preventVoicesAutoLoad = v;
}

export {
	activateVoice,
	getIsCustomVoiceActive,
	getCustomVoiceStatus,
	resumeCustomVoiceActive,
	suspendCustomVoiceActive,
	findVoice,
	findDefaultVoice,
	loadSpeechVoices,
	getVoices,
	getLanguage,
	getVoicesAdjectives,
	findVoicesByAdjectives,
	getTtsProviders,
	calculateVoiceReadingRate,
	voicesLoadingState,
	getVoiceLoadingPormises,
	setVoicesPreventAutoLoad
}

export default {
	data() {
		return {
			languages: [],
			voices: [],
			voiceStyles: [],
			voiceTypeSettings: [],
			voiceTypeModels: [],
			voiceTtsProviders: [],
			systemLanguages: [],
			allVoicesLoaded: false,
			voicesLoadingState: 0
		};
	},
	
	mounted() {
		if (__DEBUG__)
			window.cognitiveArrays = this;

		this.$nextTick(() => {
			this.loadSpeechVoices();
		});
	},
	methods: {
		activateVoice(voice, preventResume, style)	{
			return activateVoice(voice, preventResume, style);
		},
		getIsCustomVoiceActive(voiceId, style) {
			return getIsCustomVoiceActive(voiceId, style);
		},
		getCustomVoiceStatus(voiceId, style) {
			return getCustomVoiceStatus(voiceId, style);
		},
		resumeCustomVoiceActive(voiceId, style) {
			return resumeCustomVoiceActive(voiceId, style);
		},
		suspendCustomVoiceActive(voiceId, style) {
			return suspendCustomVoiceActive(voiceId, style);
		},
		findVoice(id, language, gender, isCustom, prefilteredVoices) {
			return findVoice(id, language, gender, isCustom, prefilteredVoices);
		},
		findDefaultVoice(language, gender) {
			return findDefaultVoice(language, gender)
		},
		async loadSpeechVoices(force, locale) {
			let self = this;
			let setInfo = (state) => {
				if (__DEBUG__)
					console.debug('setInfo');

				self.voicesLoadingState = state;
				self.allVoicesLoaded = state === 2;
				self.languages = loadedLanguages;
				self.voices = loadedVoices;
				self.voiceTypeSettings = voiceTypeSettings;
				self.voiceTypeModels = voiceTypeModels;
				self.voiceTtsProviders = ttsProviders;
				self.systemLanguages = systemLanguages;

				if (__DEBUG__)
					window.voices = loadedVoices;

				if (__DEBUG__)
					window.systemLanguages = systemLanguages;
			};

			let processResults = () => {
				let promises = getVoiceLoadingPormises();
				let res = null;
				if (promises.loadSpeechVoicesPromise) {
					res = promises.loadSpeechVoicesPromise.then(() => {
						setInfo(promises.loadSpeechVoicesPromise === promises.loadSpeechVoicesFinalPromise ? 2 : 1)
						return loadedVoices;
					});
				}
				if (promises.loadSpeechVoicesFinalPromise && promises.loadSpeechVoicesPromise !== promises.loadSpeechVoicesFinalPromise) {
					promises.loadSpeechVoicesFinalPromise.then(() => {
						setInfo(2);
						return loadedVoices;
					});
				}

				return res;
			}

			if (!preventVoicesAutoLoad) {
				let result = loadSpeechVoices(force, locale);

				processResults();
				return await result;
			} else {
				let promises = processResults();
				if (promises) {
					return promises;
				} else {
					return new Promise(resolve => {
						onLoad.push({
							setInfo,
							resolve
						});
					});
				}
			}
		},
		getVoices(lang) {
			return getVoices(lang);
		},
		getVoiceStyles(id) {
			let voice = this.voices.find(it => it.id === id || it.uniqueId === id);
			if (!voice) {
				if (__DEBUG__)
					console.warn('Voice not found', id);
				return [];
			}
			this.voiceStyles = voice.styleList;
			return this.voiceStyles;
		},
		getLanguage(val) {
			return getLanguage(val);
		},
		async getVoicesAdjectives() {
			return getVoicesAdjectives();
		},
		findVoicesByAdjectives(adjectives, voices) {
			return findVoicesByAdjectives(adjectives, voices);
		}
	}
};
